/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { cloneDeep, isEqual, pick } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { PageContainer } from "../../components/layout/PageContainer";
import { Loader } from "../../components/Loader";
import StatusChip from "../../components/StatusChip";
import { StatusHeader } from "../../components/StatusHeader";
import Typography from "../../components/Typography";
import YesNoAction from "../../components/YesNoAction";
import { usePermission } from "../../hooks/usePermission";
import { CartColumnView, FlexShrink } from "../../pages/CartComponents";
import apiClient from "../../services/api-client";
import { TestIds } from "../../testIds";
import { CONTENT_LAYOUT_WIDTH } from "../../util/consts";
import { selectCurrentFarmerAccount } from "../azure-b2c/storage";
import { CartItem } from "../cart/storage/cart.types";
import { invalidatePastProducts } from "../products/storage";

import {
  HandleSubmitQuoteParams,
  QuoteConfirmDetails,
} from "./components/QuoteDetails/QuoteConfirmDetails";
import { QuoteDetailsProducts } from "./components/QuoteDetails/QuoteDetailsProducts";
import QuoteSummary from "./components/QuoteDetails/QuoteSummary";
import { QuoteSummaryTotal } from "./components/QuoteDetails/QuoteSummaryTotal";
import { SendOfferModal } from "./components/QuoteDetails/SendOfferModal";
import { QuoteCreatedAt } from "./components/QuotesList/QuoteCreatedAt";
import { QuoteExpiry } from "./components/QuotesList/QuoteExpiry";
import { AcceptQuotePayload, Quote, QuoteStatus } from "./storage/types";
import { getProductsForQuote } from "./utils/getProductsForQuote";
import { mapQuoteStatus, showQuoteAsTwoColumnLayout } from "./utils/getQuoteStatus";
import { mapCartItemsToAcceptQuoteProducts } from "./utils/mapCartItemsToAcceptQuoteProducts";
import { mapCartProductsToQuoteProducts } from "./utils/mapCartProductsToQuoteProducts";

const QuoteDetails = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [originalQuote, setOriginalQuote] = useState<Quote | null>(null);
  const [products, setProducts] = useState<CartItem[]>([]);
  const [originalProducts, setOriginalProducts] = useState<CartItem[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const [error, setError] = useState(false);
  const [isNoQuoteFound, setIsNoQuoteFound] = useState(false);
  const [isSubmitQuoteModalOpen, setIsSubmitQuoteModalOpen] = useState(false);
  const [isSendOfferModalOpen, setIsSendOfferModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { quoteId } = useParams();

  const account = useSelector(selectCurrentFarmerAccount);
  const isTfo = account?.hasTfoAccess || false;
  const canAcceptOrDeclineOffer = usePermission("Quotes_QuoteAcceptDecline").allowed;

  const isQuoteModified = useMemo(() => {
    const productProperties = ["quantity", "productId", "editedPriceExcGst"];

    const productChattels = products.map((p) => pick(p, productProperties));
    const originalProductChattels = originalProducts.map((p) => pick(p, productProperties));

    const areProductsModified = !isEqual(productChattels, originalProductChattels);

    const isDeliveryModified = !isEqual(quote?.delivery, originalQuote?.delivery);
    const hasNote = note !== null;

    return areProductsModified || isDeliveryModified || hasNote;
  }, [quote, products, originalProducts, originalQuote]);

  useEffect(() => {
    if (isQuoteModified) {
      const handleBeforeUnload = (event: WindowEventMap["beforeunload"]) => {
        event.preventDefault();
      };
      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isQuoteModified]);

  const mappedStatus = React.useMemo(() => {
    if (quote) {
      return mapQuoteStatus(quote.status);
    }
    return null;
  }, [quote]);

  useEffect(() => {
    const getQuote = async () => {
      try {
        const result = await apiClient.get<Quote, AxiosError>(
          `/quotes/${quoteId}?accountNumber=${account?.accountNumber}`
        );
        if (result && result.status === 200 && result.data) {
          setQuote(result.data);
          setOriginalQuote(result.data);
          setIsNoQuoteFound(false);
        }
      } catch {
        setLoading(false);
        setIsNoQuoteFound(true);
      }
    };
    getQuote();
  }, [account?.accountNumber]);

  useEffect(() => {
    const getProducts = async () => {
      if (quote && account?.accountNumber) {
        const productResults = await getProductsForQuote(quote, account?.accountNumber);
        updateQuoteProducts(productResults);
        setOriginalProducts(cloneDeep(productResults));
        setLoading(false);
      }
    };
    getProducts();
  }, [originalQuote?.products, account?.accountNumber]);

  const updateQuoteProducts = (updatedProducts: CartItem[]): void => {
    setQuote((previousQuote: Quote | null) =>
      previousQuote
        ? { ...previousQuote, products: mapCartProductsToQuoteProducts(updatedProducts) }
        : previousQuote
    );
    setProducts(updatedProducts);
  };

  const onSend = () => {
    setIsSendOfferModalOpen(true);
  };

  const renderTitle = () => {
    if (!quote) {
      return;
    }
    if (!isSm) {
      return (
        <Stack direction="row" mb={3} justifyContent="space-between" width="100%">
          <StatusHeader
            title={`Quote #${quote.reference}`}
            statusChip={
              mappedStatus && (
                <StatusChip
                  label={mappedStatus.display}
                  variant="filled"
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.components.statusHeader.statusBadge}
                />
              )
            }
            text={<QuoteExpiry quote={quote} />}
          />

          <Stack direction="column" justifyContent="space-between" spacing={2}>
            <QuoteCreatedAt createdAt={quote.createdAt} />
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack direction="column" width={"100%"} marginBottom={"16px"}>
        <StatusHeader
          title={`Quote #${quote.reference}`}
          statusChip={
            mappedStatus && (
              <Stack
                direction={"row"}
                width={"100%"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flexWrap={"wrap"}
                sx={(theme) => ({
                  rowGap: theme.spacing(1.5),
                  columnGap: theme.spacing(2),
                })}
              >
                <StatusChip
                  label={mappedStatus.display}
                  variant="filled"
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.components.statusHeader.statusBadge}
                />
                <QuoteCreatedAt createdAt={quote.createdAt} />
              </Stack>
            )
          }
          text={<QuoteExpiry quote={quote} />}
        />
      </Stack>
    );
  };

  const renderNoQuoteFound = useMemo(
    () => (
      <Box mb={2}>
        <YesNoAction
          display="Quote not available"
          body="We are unable to find this quote."
          optionB={{
            label: "Back to Quotes",
            onClick: () => {
              navigate("/quotes");
            },
          }}
        />
      </Box>
    ),
    []
  );

  const twoColumnLayout = useMemo(() => {
    if (quote) {
      return showQuoteAsTwoColumnLayout(quote);
    }
    return false;
  }, [quote, account]);

  const handleValidation = () => {
    const totalProducts = products.reduce((prev, curr) => {
      return prev + curr.quantity;
    }, 0);
    return totalProducts > 0;
  };

  const submitAcceptQuote = async ({ name, email, phone, reference }: HandleSubmitQuoteParams) => {
    if (quote?.status === QuoteStatus.Offered && !canAcceptOrDeclineOffer) {
      return;
    }
    try {
      setSubmitting(true);
      if (!quote?.version || !account?.accountNumber) {
        return;
      }
      const result = await apiClient.post<Quote, AcceptQuotePayload>(`/quotes/${quoteId}/accept`, {
        version: quote?.version,
        accountNumber: account?.accountNumber,
        products: mapCartItemsToAcceptQuoteProducts(products),
        contact: {
          name,
          phone,
          email,
        },
        delivery: quote.delivery,
        // False positive as empty string should be regarded as falsy and return null
        customerReference: reference || null,
        note,
      });
      if (result && result.status === 200 && result.data) {
        setIsSubmitQuoteModalOpen(false);
        invalidatePastProducts(queryClient, account.accountNumber);
        navigate(`/quote-order-submitted/${result.data.reference}`, {
          state: { quotes: result.data },
        });
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (
      !account ||
      !quote ||
      (quote.status === QuoteStatus.Offered && !canAcceptOrDeclineOffer) ||
      !handleValidation()
    ) {
      return;
    }
    setError(false);
    setIsSubmitQuoteModalOpen(true);
  };

  const handleSubmitQuote = (arg: HandleSubmitQuoteParams) => {
    setError(false);
    if (
      (quote?.status === QuoteStatus.Offered && !canAcceptOrDeclineOffer) ||
      !handleValidation()
    ) {
      return;
    }
    submitAcceptQuote(arg);
  };

  return (
    <PageContainer
      data-testid={TestIds.pages.quoteDetails}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: `${CONTENT_LAYOUT_WIDTH}px`,
      }}
    >
      <BackButton text="All quotes" onClick={() => navigate("/quotes")} />
      {loading && <Loader />}
      {!loading && !isNoQuoteFound && quote && (
        <>
          {quote?.isLocked && quote.status === QuoteStatus.Offered ? (
            <Banner
              sx={{
                width: "100%",
                marginBottom: "16px",
                justifyContent: "center",
              }}
              testId={TestIds.feat.quotes.details.quoteLocked}
              symbol="lock"
              title=""
              body="The only adjustment available for this quote is increasing the product quantity"
            />
          ) : null}
          {renderTitle()}
          <Typography
            variant="body2"
            fontSize={"18px"}
            sx={{ pb: "12px" }}
            color="primary500"
            fontWeight={700}
          >
            Items requested
          </Typography>
          <CartColumnView>
            <QuoteDetailsProducts
              quote={quote}
              originalProducts={originalProducts}
              products={products}
              setProducts={updateQuoteProducts}
            />

            {twoColumnLayout ? (
              <FlexShrink>
                <QuoteSummary
                  quote={quote}
                  setQuote={setQuote}
                  handleContinue={handleContinue}
                  note={note}
                  setNote={setNote}
                  onSend={onSend}
                  submitting={submitting}
                >
                  <QuoteSummaryTotal
                    accountName={account?.accountName}
                    accountNumber={account?.accountNumber}
                    products={products}
                    originalProducts={originalProducts}
                    freightChargeExcGst={quote?.delivery?.freightChargeExcGst}
                  />
                </QuoteSummary>
              </FlexShrink>
            ) : null}
          </CartColumnView>
          <QuoteConfirmDetails
            quote={quote}
            error={error}
            isModalOpen={isSubmitQuoteModalOpen}
            setIsModalOpen={setIsSubmitQuoteModalOpen}
            submitting={submitting}
            handleSubmitQuote={handleSubmitQuote}
          />
        </>
      )}
      {!loading && isNoQuoteFound && renderNoQuoteFound}
      {isTfo && quote ? (
        <SendOfferModal
          multiple
          isModalOpen={isSendOfferModalOpen}
          quote={quote}
          products={products}
          setIsModalOpen={setIsSendOfferModalOpen}
        />
      ) : null}
    </PageContainer>
  );
};

export default QuoteDetails;
